import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import ErrorNotification from './ErrorNotification';
import { useErrorNotifications } from '../hooks/useErrorNotifications';
import { useNotificationPreferences } from '../hooks/useNotificationPreferences';

const ErrorNotificationContainer: React.FC = () => {
  const { notifications: allNotifications, removeNotification } = useErrorNotifications();
  const { popups } = useNotificationPreferences();
  const notifications = allNotifications.filter(notification => popups || notification.source !== 'notification');

  // Handle scroll position for mobile responsiveness
  useEffect(() => {
    const handleScroll = () => {
      // Scroll handling logic can be added here if needed
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle keyboard navigation for accessibility
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Close the most recent notification on Escape
        if (notifications.length > 0) {
          const latestNotification = notifications[0];
          removeNotification(latestNotification.id);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [notifications, removeNotification]);

  // Handle window resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      // Resize handling logic can be added here if needed
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (notifications.length === 0) {
    return null;
  }


  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        zIndex: 1600, // Higher than modals and other overlays
        pointerEvents: 'none', // Allow clicks to pass through container
        '& > *': {
          pointerEvents: 'auto', // Enable pointer events for individual notifications
        },
      }}
      role="region"
      aria-label="Error notifications"
      aria-live="polite"
      aria-atomic="false"
    >
      {notifications.map((notification, index) => (
        <ErrorNotification
          key={notification.id}
          notification={notification}
          onRemove={removeNotification}
          index={index}
          total={notifications.length}
        />
      ))}
    </Box>
  );
};

export default ErrorNotificationContainer;
